import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BookOpen, Users, Calendar, Clock, MapPin, Eye } from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyTeacherProfile } from "../../services/teachers";

const TeacherClasses: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  // Fetch teacher profile to get assigned classes
  const { data: teacherData, isLoading } = useQuery({
    queryKey: ["teacher-profile"],
    queryFn: () => getMyTeacherProfile(),
    enabled: !!user?.id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-blue-500"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading classes...</p>
        </div>
      </div>
    );
  }

  const teacher = teacherData?.data;
  const classes = teacher?.currentClasses || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      {/* Header */}
      <div className="mb-8 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <h1 className="text-3xl font-bold text-slate-900">My Classes</h1>
        <p className="text-slate-600 mt-2 font-medium">
          Manage your assigned classes and students
        </p>
      </div>

      {/* Classes Grid */}
      {classes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((classAssignment: any) => {
            const classInfo = classAssignment.classId;
            return (
              <div
                key={classInfo._id}
                className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6 hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-1">
                      {classInfo.name}
                    </h3>
                    <p className="text-slate-600 text-sm font-medium">
                      {classInfo.fullName || classInfo.name}
                    </p>
                    {classAssignment.isClassTeacher && (
                      <span className="inline-block bg-gradient-to-br from-blue-300 to-indigo-400 text-white text-xs px-3 py-1 rounded-xl font-bold shadow-sm mt-2">
                        Class Teacher
                      </span>
                    )}
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center shadow-sm">
                    <BookOpen className="h-5 w-5 text-white" />
                  </div>
                </div>

                {/* Class Stats */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-slate-600 font-medium">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center mr-2 shadow-sm">
                      <Users className="h-4 w-4 text-white" />
                    </div>
                    <span>
                      {classInfo.currentEnrollment || 0} /{" "}
                      {classInfo.capacity || 0} students
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-slate-600 font-medium">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center mr-2 shadow-sm">
                      <MapPin className="h-4 w-4 text-white" />
                    </div>
                    <span>Grade: {classInfo.grade || "Not specified"}</span>
                  </div>
                  <div className="flex items-center text-sm text-slate-600 font-medium">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center mr-2 shadow-sm">
                      <Calendar className="h-4 w-4 text-white" />
                    </div>
                    <span>
                      Started:{" "}
                      {new Date(classAssignment.startDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-slate-600 font-medium">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mr-2 shadow-sm">
                      <Clock className="h-4 w-4 text-white" />
                    </div>
                    <span>
                      {classAssignment.courses?.length || 0} assigned courses
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <Link
                    to={`/teacher/classes/${classInfo._id}`}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-gradient-to-br from-blue-300 to-indigo-400 text-white text-sm font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View Details
                  </Link>
                  <Link
                    to={`/teacher/attendance?classId=${classInfo._id}`}
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    <Calendar className="h-4 w-4 mr-1" />
                    Attendance
                  </Link>
                </div>

                {/* Progress Bar */}
                {classInfo.capacity && (
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-slate-600 font-semibold mb-1">
                      <span>Capacity</span>
                      <span>
                        {classInfo.capacity ? (
                          <>
                            {Math.round(
                              ((classInfo.currentEnrollment || 0) /
                                classInfo.capacity) *
                                100
                            )}
                            %
                          </>
                        ) : (
                          "N/A"
                        )}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5 shadow-inner">
                      <div
                        className="bg-gradient-to-r from-blue-400 to-indigo-500 h-2.5 rounded-full shadow-sm transition-all duration-500"
                        style={{
                          width: `${
                            classInfo.capacity
                              ? Math.min(
                                  ((classInfo.currentEnrollment || 0) /
                                    classInfo.capacity) *
                                    100,
                                  100
                                )
                              : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center mx-auto shadow-lg">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          <h3 className="mt-6 text-lg font-bold text-slate-900">
            No Classes Assigned
          </h3>
          <p className="mt-2 text-slate-600 font-medium max-w-md mx-auto">
            You haven't been assigned to any classes yet. Contact your
            administrator to get assigned to classes.
          </p>
        </div>
      )}
    </div>
  );
};

export default TeacherClasses;
